<template>
  <div class="stock-lending" data-testid="stock-lending">
    <template v-if="!error">
      <StockLendingFilters
        class="stock-lending__filters"
        data-testid="stock-lending-filters"
        :filters="filters"
        :updated-at="updatedAt"
        :is-loading="!hasFilters && isLoading"
        :error="error"
        :total-items="totalItems"
        @on-filter-change="handleOnFilterChange"
      />
    </template>

    <template v-if="error">
      <HelperViewStatus
        class="stock-lending__error"
        title="Não foi possível carregar os seus ativos"
        description="Aguarde um momento e tente novamente"
        button-label="Tentar novamente"
        status="alert"
        @click="fetchStockLendingPositions"
      />
    </template>

    <template v-else-if="hasPositions || isLoading">
      <StockLendingTable
        data-testid="stock-lending-table"
        :items="positions"
        :items-per-page="itemsPerPage"
        :is-loading="isLoading"
      />
      <Pagination
        v-if="hasPositions || isLoading"
        data-testid="stock-lending-pagination"
        class="stock-lending__pagination"
        :total-pages="totalPages"
        :current-page="currentPage"
        :is-loading="isLoading"
        @page-changed="handlePageChange"
      />
    </template>

    <template v-else-if="!hasPositions && !isLoading">
      <HelperViewContextual
        class="stock-lending__helper"
        icon="ED0090"
        description="Quando seus ativos forem alugados, eles aparecerão aqui automaticamente."
      />
    </template>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';

import { StockLendingTable } from './components/StockLendingTable';
import { StockLendingFilters } from './components/StockLendingFilters';
import { Pagination } from '@/modules/common/components/Pagination';
import {
  IFilterOption,
  HelperViewStatus,
  HelperViewContextual
} from '@warrenbrasil/nebraska-web';

import { IStockLendingFilters, StockLendingFiltersTypes } from './types';
import { StringFormatted } from '@/modules/trade/types';
import {
  StockLendingPosition,
  getStockLendingList
} from '@/modules/trade/services/instruments';

@Component({
  components: {
    StockLendingTable,
    StockLendingFilters,
    Pagination,
    HelperViewStatus,
    HelperViewContextual
  }
})
export default class StockLending extends Vue {
  filters: IStockLendingFilters = {
    [StockLendingFiltersTypes.Type]: {
      selected: null,
      available: []
    }
  };

  abortSignal: AbortController | null = null;
  isLoading = true;
  error = false;
  itemsPerPage = 10;
  currentPage = 1;
  totalPages = 1;
  positions: StockLendingPosition[] | null = null;
  updatedAt: StringFormatted = {
    value: '-',
    formatted: '-'
  };

  isActive = false;
  totalItems = '';

  get hasPositions() {
    return this.positions && this.positions.length > 0;
  }

  get paidCustodyActive() {
    return this.isActive;
  }

  @Watch('hasPositions', { immediate: true })
  onHasPositionsChange(newValue: boolean) {
    this.$emit('update:hasPositions', newValue);
  }

  @Watch('paidCustodyActive', { immediate: true })
  onPaidCustodyActive(newValue: boolean) {
    this.$emit('update:paidCustodyActive', newValue);
  }

  get hasFilters() {
    return this.filters[StockLendingFiltersTypes.Type].available?.length > 0;
  }

  async created() {
    await this.fetchStockLendingPositions();
  }

  async fetchStockLendingPositions() {
    try {
      this.isLoading = true;
      this.error = false;
      if (this.abortSignal) this.abortSignal.abort();
      this.abortSignal = new AbortController();

      const { items, pagination, updatedAt, totalItems, isActive, filters } =
        await getStockLendingList(
          {
            itemsPerPage: this.itemsPerPage,
            page: this.currentPage,
            side: this.filters[StockLendingFiltersTypes.Type].selected
          },
          this.abortSignal.signal
        );

      this.itemsPerPage = pagination.itemsPerPage;
      this.currentPage = pagination.page;
      this.totalPages = pagination.totalPages;

      this.updatedAt = updatedAt;
      this.totalItems = totalItems.formatted;
      this.isActive = isActive;

      this.positions = items;

      this.filters[StockLendingFiltersTypes.Type].available = filters.available;
      this.filters[StockLendingFiltersTypes.Type].selected = filters.selected;
    } catch {
      this.error = true;
    } finally {
      this.isLoading = false;
    }
  }

  async handleOnFilterChange(
    filterKey: StockLendingFiltersTypes,
    filter: IFilterOption
  ) {
    this.filters[filterKey].selected = filter.value;
    await this.fetchStockLendingPositions();
  }

  async handlePageChange(page: number) {
    this.currentPage = page;
    await this.fetchStockLendingPositions();
  }
}
</script>

<style lang="less" scoped>
.stock-lending {
  padding: @size-spacing-x600 @size-spacing-x500 0 @size-spacing-x500;

  .to-lg({ padding: @size-spacing-x400 @size-spacing-x400;});

  &__filters {
    margin-bottom: @size-spacing-x500;
  }

  &__pagination {
    margin: @size-spacing-x500 0;
  }

  &__error {
    margin: @size-spacing-x1800 auto @size-spacing-x400 auto;
  }

  &__helper {
    margin: @size-spacing-x1800 auto @size-spacing-x400 auto;
  }
}
</style>
